import type { RewriteTier } from '@lyrmo/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../api/client';
import { plainText } from './markdown';

/** What's read aloud: one of the versions, or the draft itself. */
export type SpeechSource = RewriteTier | 'draft';

export type SpeechState =
  | { readonly source: SpeechSource; readonly status: 'loading' | 'playing' }
  | { readonly source: SpeechSource; readonly status: 'error'; readonly error: unknown }
  | null;

export type Speech = ReturnType<typeof useSpeech>;

/** One voice at a time; audio stays only for the current set of corrections. */
export function useSpeech(generate: ApiClient['createSpeech'] | undefined, requestId: number) {
  const [current, setCurrent] = useState<{
    id: number;
    generate: ApiClient['createSpeech'];
    state: SpeechState;
  } | null>(null);
  const controller = useRef<AbortController | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const url = useRef<string | null>(null);
  const cache = useRef(new Map<string, Blob>());

  const release = useCallback(() => {
    controller.current?.abort();
    controller.current = null;
    if (audio.current) {
      audio.current.onended = null;
      audio.current.onerror = null;
      audio.current.pause();
      audio.current.src = '';
      audio.current = null;
    }
    if (url.current) URL.revokeObjectURL(url.current);
    url.current = null;
  }, []);

  useEffect(() => {
    const savedAudio = cache.current;
    return () => {
      release();
      savedAudio.clear();
    };
  }, [generate, requestId, release]);

  const stop = useCallback(() => {
    release();
    setCurrent(null);
  }, [release]);

  const speak = useCallback(
    async (source: SpeechSource, text: string) => {
      if (!generate) return;
      release();
      const pending = new AbortController();
      controller.current = pending;
      const update = (state: SpeechState) => {
        setCurrent({ id: requestId, generate, state });
      };
      update({ source, status: 'loading' });
      try {
        let blob = cache.current.get(text);
        if (!blob) {
          // The words alone: what formats them isn't read out.
          blob = await generate(plainText(text), { signal: pending.signal });
          if (pending.signal.aborted) return;
          cache.current.set(text, blob);
        }
        const player = new Audio();
        audio.current = player;
        url.current = URL.createObjectURL(blob);
        player.src = url.current;
        player.onended = () => {
          release();
          update(null);
        };
        player.onerror = () => {
          release();
          cache.current.delete(text);
          update({ source, status: 'error', error: new Error('Playback failed') });
        };
        await player.play();
        if (!pending.signal.aborted) update({ source, status: 'playing' });
      } catch (error) {
        if (pending.signal.aborted) return;
        release();
        update({ source, status: 'error', error });
      }
    },
    [generate, requestId, release],
  );

  return {
    state: current?.id === requestId && current.generate === generate ? current.state : null,
    speak,
    stop,
  };
}
